import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Autocomplete, Box, Chip, Typography, CircularProgress, Divider, InputAdornment } from '@mui/material'
import { FormInput } from '../ui'
import { Business as BusinessIcon, Add as AddIcon } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import newLeadLogo from '../../assests/drawerIcons/newleadLogo.svg'
import { leadSchema } from '../../lib/schemas/leadSchema'
import { LEAD_SOURCES, SOURCE_LABELS, INDUSTRY_TYPES } from '../../lib/api/leadsApi'
import { getCustomerTypes, CustomerType } from '../../lib/api/customerTypesApi'
import { customersApi } from '../../lib/api/customersApi'
import { orgsApi, lobsApi } from '../../lib/api/tenantsApi'
import { authStore } from '../../lib/stores/authStore'
import { themeColors } from '../../app/layout/theme'
import { useOrgSelector } from '../../context/OrgSelectorContext'
import { gstApi } from '../../lib/api/gstApi'
import { maskPhoneNumber } from '../../utils/phoneUtils'
import {
  EntityFormDrawer,
  EntityFormSection,
  EntityDrawerInput,
  EntityDrawerSelect,
  EntityFormField,
  entityDrawerInputSx
} from '../shared/EntityFormDrawer'

type LeadFormData = z.infer<typeof leadSchema>

const SOURCE_OPTIONS = LEAD_SOURCES.map(s => ({ label: SOURCE_LABELS[s] ?? s, value: s }))

interface LeadDrawerProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: LeadFormData) => void
  isSubmitting?: boolean
  initialData?: Partial<LeadFormData>
}

export function LeadDrawer({ open, onClose, onSubmit, isSubmitting = false, initialData }: LeadDrawerProps) {
  const { selectedOrgId: globalSelectedOrgId } = useOrgSelector()
  const [fullName, setFullName] = useState('')
  const [companySearch, setCompanySearch] = useState('')

  // ── Derive user tier from authStore ──────────────────────────────────────
  // SuperAdmin : no orgId, no lobId  → free selection of any org/lob
  // OrgAdmin   : has orgId, no lobId → org locked, lob selectable within org
  // Normal     : has orgId + lobId   → both locked
  const currentUser  = authStore.user
  const userOrgId    = currentUser?.orgId  || ''
  const userLobId    = currentUser?.lobId  || ''
  const isSuperAdmin = !userOrgId && !userLobId
  const isOrgAdmin   = !!userOrgId && !userLobId
  const isNormalUser = !!userOrgId && !!userLobId
  const orgLocked    = isOrgAdmin || isNormalUser   // disable org dropdown
  const lobLocked    = isNormalUser                  // disable lob dropdown

  // GST Validation States
  const [gstLoading, setGstLoading] = useState(false)
  const [gstFeedback, setGstFeedback] = useState<{ type: 'success' | 'warning' | 'error'; text: string } | null>(null)
  const [lastValidatedGst, setLastValidatedGst] = useState('')

  const { handleSubmit, watch, setValue, formState: { errors }, reset } = useForm<LeadFormData>({
    resolver: zodResolver(leadSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '', lastName: '', email: '', contactNumber: '',
      source: undefined, owner: authStore.user?.name || authStore.user?.email || '',
      companyName: '',
      orgId: '', lobId: '', warehouseId: '',
      description: '',
      bestTimeToContact: '',
      requirements: '',
      designation: '', companyWebsite: '', industry: '',
      customerTypeId: undefined,
      fileType: '',
      expectedCloseDate: '',
      expectedAmount: undefined,
    },
  })

  const selectedOrgId = watch('orgId')
  const selectedLobId = watch('lobId')

  // Sync fullName with initialData if editing
  useEffect(() => {
    if (initialData) {
      const name = `${initialData.firstName || ''} ${initialData.lastName || ''}`.trim()
      setFullName(name)
      reset({
        ...initialData,
        owner: initialData.owner || authStore.user?.name || authStore.user?.email || '',
        orgId: initialData.orgId || globalSelectedOrgId || '',
        lobId: initialData.lobId || '',
        warehouseId: initialData.warehouseId || '',
        description: initialData.description || '',
        bestTimeToContact: (initialData as any).bestTimeToContact || '',
        requirements: (initialData as any).requirements || '',
        designation: (initialData as any).designation || '',
        companyWebsite: (initialData as any).companyWebsite || '',
        industry: (initialData as any).industry || '',
        customerTypeId: (initialData as any).customerTypeId ?? undefined,
        fileType: (initialData as any).fileType || '',
        gst: (initialData as any).gst || '',
        address: (initialData as any).address || '',
        pincode: (initialData as any).pincode || '',
        expectedCloseDate: (initialData as any).expectedCloseDate
          ? (initialData as any).expectedCloseDate.split('T')[0]
          : '',
        expectedAmount: (initialData as any).expectedAmount ?? undefined,
      })
    } else {
      setFullName('')
      reset({
        firstName: '', lastName: '', email: '', contactNumber: '',
        source: undefined, owner: authStore.user?.name || authStore.user?.email || '',
        companyName: '',
        // Pre-fill org/lob for Admin and Normal users; SuperAdmin falls back to global selector
        orgId: userOrgId || globalSelectedOrgId || '',
        lobId: userLobId || '',
        warehouseId: '',
        description: '',
        bestTimeToContact: '',
        requirements: '',
        designation: '', companyWebsite: '', industry: '',
        customerTypeId: undefined,
        fileType: '',
        expectedCloseDate: '',
        expectedAmount: undefined,
      })
    }
  }, [initialData, reset, open, globalSelectedOrgId, userOrgId, userLobId])

  // Reset GST validation states when drawer is opened/closed
  useEffect(() => {
    if (open) {
      setGstFeedback(null)
      setLastValidatedGst('')
      setGstLoading(false)
    }
  }, [open])

  const gstValue = watch('gst')

  useEffect(() => {
    if (!gstValue) {
      setGstFeedback(null)
      return
    }

    const trimmed = gstValue.trim().toUpperCase()

    // Clear feedback if user edits
    if (trimmed !== lastValidatedGst) {
      setGstFeedback(null)
    }

    if (trimmed.length !== 15) {
      return
    }

    // Check format
    const formatRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i
    if (!formatRegex.test(trimmed)) {
      setGstFeedback({ type: 'error', text: 'Invalid GST Number.' })
      return
    }

    if (trimmed === lastValidatedGst) {
      return
    }

    const timer = setTimeout(async () => {
      setGstLoading(true)
      setGstFeedback(null)
      try {
        const result = await gstApi.validateGst(trimmed)
        setLastValidatedGst(trimmed)
        if (result.isValid && result.status?.toLowerCase() === 'active') {
          setGstFeedback({ type: 'success', text: 'GST verified successfully.' })
          if (result.legalName) {
            setValue('companyName', result.legalName, { shouldValidate: true })
          }
          if (result.address) {
            setValue('address', result.address, { shouldValidate: true })
          }
          if (result.pincode) {
            setValue('pincode', result.pincode, { shouldValidate: true })
          }
        } else if (result.status && result.status.toLowerCase() !== 'active') {
          let warningMsg = `GST is not active.\n\nStatus:\n${result.status}`
          if (result.cancellationDate) {
            let formattedDate = result.cancellationDate
            try {
              const dateObj = new Date(result.cancellationDate)
              if (!isNaN(dateObj.getTime())) {
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}-${months[dateObj.getMonth()]}-${dateObj.getFullYear()}`
              }
            } catch (e) {
              // ignore
            }
            warningMsg += `\n\nCancellation Date:\n${formattedDate}`
          }
          setGstFeedback({ type: 'warning', text: warningMsg })
        } else {
          setGstFeedback({ type: 'error', text: 'Invalid GST Number.' })
        }
      } catch (err) {
        setGstFeedback({ type: 'error', text: 'Invalid GST Number.' })
      } finally {
        setGstLoading(false)
      }
    }, 700)

    return () => clearTimeout(timer)
  }, [gstValue, lastValidatedGst, setValue])

  const { data: companySuggestions } = useQuery({
    queryKey: ['company-suggestions', companySearch],
    queryFn: () => customersApi.getAll({ companyNameSearch: companySearch, pageSize: 5 }),
    enabled: companySearch.length > 1
  })

  // ── Tenant Queries ──
  // SuperAdmin fetches all orgs; locked users don't need the full list
  const { data: orgs = [] } = useQuery({
    queryKey: ['orgs'],
    queryFn: () => orgsApi.getAll(),
    enabled: open && isSuperAdmin,
  })

  // OrgAdmin uses their own orgId; SuperAdmin uses the selected org from the form
  const orgIdForLobs = orgLocked ? userOrgId : selectedOrgId
  const { data: lobs = [] } = useQuery({
    queryKey: ['lobs', orgIdForLobs],
    queryFn: () => lobsApi.getAll(orgIdForLobs),
    enabled: !!orgIdForLobs && !lobLocked,
  })

  const { data: customerTypes = [] } = useQuery<CustomerType[]>({
    queryKey: ['customer-types'],
    queryFn: getCustomerTypes,
    staleTime: 5 * 60 * 1000,
  })

  const f = (name: keyof LeadFormData) => (val: string) => setValue(name, val as any, { shouldValidate: true })

  const handleFullNameChange = (val: string) => {
    setFullName(val)
    const parts = val.trim().split(/\s+/)
    if (parts.length > 0) {
      setValue('firstName', parts[0], { shouldValidate: true })
      setValue('lastName', parts.slice(1).join(' '), { shouldValidate: true })
    }
  }

  const onFormSubmit = (data: LeadFormData) => {
    // Final check for names
    const parts = fullName.trim().split(/\s+/)
    data.firstName = parts[0] || ''
    data.lastName = parts.slice(1).join(' ')

    if (!data.source) {
      setValue('source', undefined, { shouldValidate: true })
      return
    }

    onSubmit(data)
  }

  return (
    <EntityFormDrawer
      open={open}
      title={initialData ? 'Edit Lead' : 'New Lead'}
      icon={<img src={newLeadLogo} alt="lead" style={{ width: 20, height: 20 }} />}
      onClose={onClose}
      onSubmit={handleSubmit(onFormSubmit)}
      submitLabel={initialData ? 'Save Changes' : 'Create Lead'}
      isSubmitting={isSubmitting}
    >
      <EntityFormSection title="" columns={2}>
        {/* Row 1: Full Name | Contact Number */}
        <EntityDrawerInput
          label="Full Name"
          required
          value={fullName}
          onChange={handleFullNameChange}
          placeholder="Enter full name"
          error={errors.firstName?.message || errors.lastName?.message}
        />

        <EntityDrawerInput
          label="Contact Number"
          required
          value={watch('contactNumber')}
          onChange={f('contactNumber')}
          fieldType="phone"
          placeholder="Enter contact number"
          error={errors.contactNumber?.message}
        />

        {/* Row 2: Email | Designation */}
        <EntityDrawerInput
          label="Email"
          value={watch('email') ?? ''}
          onChange={f('email')}
          placeholder="Enter email"
          error={errors.email?.message}
        />

        <EntityDrawerInput
          label="Designation"
          value={watch('designation') || ''}
          onChange={f('designation')}
          placeholder="e.g. Manager, Director"
          error={errors.designation?.message}
        />

        {/* Row 3: Industry | Customer Type */}
        <EntityDrawerSelect
          label="Industry"
          value={watch('industry') || ''}
          onChange={f('industry')}
          options={INDUSTRY_TYPES.map(i => ({ label: i, value: i }))}
          placeholder="Select industry"
          error={errors.industry?.message}
        />

        <EntityDrawerSelect
          label="Customer Type"
          value={watch('customerTypeId') != null ? String(watch('customerTypeId')) : ''}
          onChange={(v) => setValue('customerTypeId', v ? Number(v) : undefined, { shouldValidate: true })}
          options={customerTypes.map(ct => ({ label: ct.name, value: String(ct.id) }))}
          placeholder="Select customer type"
        />

        {/* Row 4: Source (full row or paired as needed) */}
        <EntityDrawerSelect
          label="Source"
          required
          value={watch('source')}
          onChange={f('source')}
          options={SOURCE_OPTIONS}
          placeholder="Select source"
          error={errors.source?.message}
        />

        {/* Row 4: GST Number | Company Name */}
        <EntityDrawerInput
          label="GST Number"
          fieldType="gstin"
          value={watch('gst') || ''}
          onChange={f('gst')}
          placeholder="e.g. 27AADCB2230M1Z2"
          error={gstFeedback?.type === 'error' ? gstFeedback.text : errors.gst?.message}
          helperText={
            gstFeedback?.type === 'success' || gstFeedback?.type === 'warning'
              ? gstFeedback.text
              : undefined
          }
          InputProps={{
            endAdornment: gstLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiFormHelperText-root': {
              color: gstFeedback?.type === 'success'
                ? '#16a34a !important'
                : gstFeedback?.type === 'warning'
                ? '#ea580c !important'
                : undefined,
              fontWeight: gstFeedback ? 600 : undefined,
              whiteSpace: 'pre-wrap',
            }
          }}
        />

        <EntityFormField label="Company Name" required>
          <Autocomplete
            freeSolo
            options={companySuggestions?.items || []}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option
              return option.companyName || option.name || ''
            }}
            value={watch('companyName') || ''}
            onChange={(_e, val) => {
              if (typeof val === 'string') {
                f('companyName')(val)
                const matchedCompany = companySuggestions?.items?.find(
                  c => (c.companyName || c.name || '').trim().toLowerCase() === val.trim().toLowerCase()
                )
                if (matchedCompany) {
                  // Organisation is only ours to fill in when the field isn't locked
                  // (SuperAdmin only — OrgAdmin/Normal users are pinned to their own org).
                  if (!orgLocked && matchedCompany.orgId)
                    setValue('orgId', matchedCompany.orgId, { shouldValidate: true })
                  // Line of Business: fill it in for SuperAdmin and OrgAdmin (anyone who
                  // can actually pick a LOB) — Normal users keep their own locked LOB.
                  if (!lobLocked && matchedCompany.lobId)
                    setValue('lobId', matchedCompany.lobId, { shouldValidate: true })
                  if (matchedCompany.warehouseId) setValue('warehouseId', matchedCompany.warehouseId, { shouldValidate: true })
                  if (matchedCompany.address) setValue('address', matchedCompany.address, { shouldValidate: true })
                  if (matchedCompany.gst) setValue('gst', matchedCompany.gst, { shouldValidate: true })
                  if (matchedCompany.pincode) setValue('pincode', matchedCompany.pincode, { shouldValidate: true })
                }
              } else if (val) {
                const name = val.companyName || val.name || ''
                f('companyName')(name)
                // Organisation is only ours to fill in when the field isn't locked
                // (SuperAdmin only — OrgAdmin/Normal users are pinned to their own org).
                if (!orgLocked && val.orgId) setValue('orgId', val.orgId, { shouldValidate: true })
                // Line of Business: fill it in for SuperAdmin and OrgAdmin (anyone who
                // can actually pick a LOB) — Normal users keep their own locked LOB.
                if (!lobLocked && val.lobId) setValue('lobId', val.lobId, { shouldValidate: true })
                if (val.warehouseId) setValue('warehouseId', val.warehouseId, { shouldValidate: true })
                if (val.address) setValue('address', val.address, { shouldValidate: true })
                if (val.gst) setValue('gst', val.gst, { shouldValidate: true })
                if (val.pincode) setValue('pincode', val.pincode, { shouldValidate: true })
              }
            }}
            onInputChange={(_e, val) => {
              setCompanySearch(val)
              f('companyName')(val)
            }}
            renderInput={(params) => (
              <FormInput
                {...params}
                placeholder="Search or enter company name"
                error={!!errors.companyName}
                helperText={errors.companyName?.message}
                sx={entityDrawerInputSx}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ py: 1, px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, lineHeight: 1.3 }}>
                    {option.companyName || option.name}
                  </Typography>
                  <Chip
                    label="Exists"
                    size="small"
                    sx={{ height: 20, fontSize: '10px', fontWeight: 700, bgcolor: '#fee2e2', color: '#ef4444', borderRadius: '4px', flexShrink: 0 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, ml: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {maskPhoneNumber(option.phone)}{option.city ? ` • ${option.city}` : ''}
                  </Typography>
                  {(option.orgName || option.lobName) && (
                    <>
                      <Typography sx={{ fontSize: '10px', color: 'text.disabled', flexShrink: 0 }}>·</Typography>
                      {option.orgName && (
                        <Chip
                          label={option.orgName}
                          size="small"
                          sx={{ height: 18, fontSize: '10px', fontWeight: 600, bgcolor: '#ede9f7', color: '#4B3082', borderRadius: '4px', flexShrink: 0 }}
                        />
                      )}
                      {option.orgName && option.lobName && (
                        <Typography sx={{ fontSize: '10px', color: 'text.disabled', flexShrink: 0 }}>›</Typography>
                      )}
                      {option.lobName && (
                        <Chip
                          label={option.lobName}
                          size="small"
                          sx={{ height: 18, fontSize: '10px', fontWeight: 600, bgcolor: '#e8f4fd', color: '#0369a1', borderRadius: '4px', flexShrink: 0 }}
                        />
                      )}
                    </>
                  )}
                </Box>
              </Box>
            )}
            noOptionsText={
              companySearch.length > 1 ? (
                <Box sx={{ py: 1, px: 2, cursor: 'pointer', color: themeColors.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AddIcon sx={{ fontSize: 18 }} />
                  <Typography variant="body2" fontWeight={600}>Create "{companySearch}"</Typography>
                </Box>
              ) : "Type to search..."
            }
          />
        </EntityFormField>

        {/* Row 5: Address (full width) */}
        <EntityDrawerInput
          label="Address"
          value={watch('address') || ''}
          onChange={f('address')}
          placeholder="e.g. 123 Main St"
          error={errors.address?.message}
          fullWidth
        />

        {/* Row 6: Pincode | Website URL */}
        <EntityDrawerInput
          label="Pincode"
          fieldType="pincode"
          value={watch('pincode') || ''}
          onChange={f('pincode')}
          placeholder="e.g. 400001"
          error={errors.pincode?.message}
        />

        <EntityDrawerInput
          label="Website URL"
          value={watch('companyWebsite') || ''}
          onChange={f('companyWebsite')}
          placeholder="e.g. https://example.com"
          error={errors.companyWebsite?.message}
        />

        {/* Row 7: Description (full width) */}
        <EntityDrawerInput
          label="Description"
          value={watch('description') || ''}
          onChange={f('description')}
          placeholder="Enter lead description..."
          multiline
          rows={3}
          error={errors.description?.message}
          fullWidth
        />

        {/* Best Time to Contact */}
        <EntityDrawerInput
          label="Best Time to Contact"
          value={watch('bestTimeToContact') || ''}
          onChange={f('bestTimeToContact')}
          placeholder="e.g. Weekdays 10 AM - 12 PM"
          error={errors.bestTimeToContact?.message}
          fullWidth
        />

        {/* Expected Close Date & Expected Order Value */}
        <EntityDrawerInput
          label="Expected Close Date"
          type="date"
          value={watch('expectedCloseDate') || ''}
          onChange={f('expectedCloseDate')}
          error={errors.expectedCloseDate?.message}
        />

        <EntityDrawerInput
          label="Expected Order Value (₹)"
          type="number"
          value={watch('expectedAmount') != null ? String(watch('expectedAmount')) : ''}
          onChange={(v) => setValue('expectedAmount', v !== '' ? Number(v) : undefined, { shouldValidate: true })}
          placeholder="e.g. 50000"
          error={errors.expectedAmount?.message}
        />

        {/* Requirements */}
        <EntityDrawerInput
          label="Requirements"
          value={watch('requirements') || ''}
          onChange={f('requirements')}
          placeholder="Enter lead requirements..."
          multiline
          rows={3}
          error={errors.requirements?.message}
          fullWidth
        />
      </EntityFormSection>

      <Divider sx={{ my: 3 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
          Tenant Assignment
        </Typography>
      </Divider>

      <EntityFormSection columns={1}>
        {/* Organisation — locked for OrgAdmin and Normal users */}
        <EntityDrawerSelect
          label="Organisation"
          required
          value={watch('orgId') || ''}
          onChange={(v) => {
            if (orgLocked) return
            setValue('orgId', v, { shouldValidate: true })
            setValue('lobId', '', { shouldValidate: true })
          }}
          options={
            orgLocked
              ? [{ label: currentUser?.orgName || userOrgId, value: userOrgId }]
              : orgs.map(o => ({ label: o.name, value: o.id }))
          }
          placeholder="Select an organisation"
          disabled={orgLocked}
          error={errors.orgId?.message}
        />

        {/* Line of Business — locked for Normal users; selectable for OrgAdmin */}
        <EntityDrawerSelect
          label="Line of Business"
          required
          value={watch('lobId') || ''}
          onChange={(v) => {
            if (lobLocked) return
            setValue('lobId', v, { shouldValidate: true })
          }}
          options={
            lobLocked
              ? [{ label: currentUser?.lobName || userLobId, value: userLobId }]
              : lobs.map(l => ({ label: l.name, value: l.id }))
          }
          placeholder={
            lobLocked
              ? (currentUser?.lobName || 'Assigned LOB')
              : (orgIdForLobs ? 'Select a line of business' : 'Select organisation first')
          }
          disabled={lobLocked || (!orgIdForLobs && !lobLocked)}
          error={errors.lobId?.message}
        />
      </EntityFormSection>
    </EntityFormDrawer>
  )
}

export default LeadDrawer
